/* Personalized Dashboard Welcome — a clean text block directly under the portal
   nav (matches the reference greeting). Shows ONE high-priority item with a
   direct action by default; "View N more" expands a compact prioritized list;
   each item has a discreet Ignore with an Undo toast. Quiet state when empty.

   Items (already prioritized + authorization-scoped by the caller):
     { signature, lead, actions:[{label, onClick, primary, href}], dateText }
   `ignore`/`unignore` persist per-user across logins via /api/welcome/*. */
import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const greetingFor = (name) => {
  const h = new Date().getHours();
  const part = h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  return `${part}, ${name || "there"}`;
};

const postIgnore = (path, signature, headers) =>
  axios.post(`${API}/api/welcome/${path}`, { signature }, { headers, withCredentials: true }).catch(() => {});

const ItemActions = ({ item, onIgnore }) => (
  <span className="text-[15px] text-slate-600 leading-relaxed">
    {item.actions.map((a, i) => (
      <React.Fragment key={i}>
        {a.href ? (
          <a href={a.href} className={a.primary ? "font-semibold text-[#00698a] hover:underline" : "text-slate-600 hover:underline"} data-testid={`welcome-action-${item.signature}-${i}`}>{a.label}</a>
        ) : (
          <button type="button" onClick={a.onClick} className={a.primary ? "font-semibold text-[#00698a] hover:underline" : "text-slate-600 hover:underline"} data-testid={`welcome-action-${item.signature}-${i}`}>{a.label}</button>
        )}
        <span className="text-slate-300"> · </span>
      </React.Fragment>
    ))}
    <button type="button" onClick={() => onIgnore(item)} className="text-slate-400 hover:text-slate-600 hover:underline" data-testid={`welcome-ignore-${item.signature}`}>Ignore</button>
  </span>
);

export function WelcomeSection({ name, items: initialItems, headers, testid = "welcome-section", whatsNew = false }) {
  const [ignored, setIgnored] = useState(new Set());
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/welcome/ignored`, { headers, withCredentials: true })
      .then((r) => { const sigs = r.data?.signatures || []; if (sigs.length) setIgnored(new Set(sigs)); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = useMemo(
    () => (initialItems || []).filter((it) => !ignored.has(it.signature)),
    [initialItems, ignored]
  );

  const doIgnore = (item) => {
    setIgnored((prev) => new Set(prev).add(item.signature));
    postIgnore("ignore", item.signature, headers);
    toast("Item ignored", {
      description: "It won't show here again.",
      action: {
        label: "Undo",
        onClick: () => {
          setIgnored((prev) => { const n = new Set(prev); n.delete(item.signature); return n; });
          postIgnore("unignore", item.signature, headers);
        },
      },
    });
  };

  const hero = items[0];
  const rest = items.slice(1);

  return (
    <div className="mb-6" data-testid={testid}>
      <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">{greetingFor(name)}</h1>

      {!hero ? (
        <p className="mt-2 flex items-center gap-2 text-[15px] text-slate-500" data-testid="welcome-quiet-state">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" /> You're all caught up.
        </p>
      ) : whatsNew ? (
        <div className="mt-2 space-y-1.5">
          <p className="text-[15px] text-slate-700 leading-relaxed" data-testid="welcome-hero">
            <b className="text-slate-900">What's new:</b> {hero.lead}{" "}
            {rest.length > 0 && !expanded && (
              <button type="button" onClick={() => setExpanded(true)} className="ml-2 min-h-[44px] sm:min-h-0 text-[13.5px] font-semibold text-[#005f7b] underline" data-testid="welcome-view-more">View {rest.length} more</button>
            )}
            <button type="button" onClick={() => doIgnore(hero)} className="ml-3 min-h-[44px] sm:min-h-0 text-[13.5px] font-medium text-[#4b5563] underline" data-testid={`welcome-ignore-${hero.signature}`}>Ignore</button>
          </p>
          {expanded && rest.length > 0 && (
            <ul className="mt-2 space-y-2 border-l-2 border-slate-100 pl-3" data-testid="welcome-expanded-list">
              {rest.map((it) => (
                <li key={it.signature} className="text-[14px] text-slate-700 leading-relaxed" data-testid={`welcome-item-${it.signature}`}>
                  {it.lead}{" "}<ItemActions item={it} onIgnore={doIgnore} />
                </li>
              ))}
              <li><button type="button" onClick={() => setExpanded(false)} className="text-[13px] font-semibold text-[#005f7b] underline" data-testid="welcome-view-less">Show less</button></li>
            </ul>
          )}
        </div>
      ) : (
        <div className="mt-2 space-y-1.5">
          <p className="text-[15px] text-slate-700 leading-relaxed" data-testid="welcome-hero">
            {hero.lead}{hero.dateText ? <span className="text-slate-500"> {hero.dateText}</span> : null}{" "}
            <ItemActions item={hero} onIgnore={doIgnore} />
          </p>

          {rest.length > 0 && !expanded && (
            <button type="button" onClick={() => setExpanded(true)} className="text-[13px] font-medium text-slate-500 hover:text-slate-700 hover:underline" data-testid="welcome-view-more">
              View {rest.length} more
            </button>
          )}

          {expanded && rest.length > 0 && (
            <ul className="mt-2 space-y-2 border-l-2 border-slate-100 pl-3" data-testid="welcome-expanded-list">
              {rest.map((it) => (
                <li key={it.signature} className="text-[14px] text-slate-700 leading-relaxed" data-testid={`welcome-item-${it.signature}`}>
                  {it.lead}{it.dateText ? <span className="text-slate-500"> {it.dateText}</span> : null}{" "}
                  <ItemActions item={it} onIgnore={doIgnore} />
                </li>
              ))}
              <li>
                <button type="button" onClick={() => setExpanded(false)} className="text-[13px] font-medium text-slate-500 hover:text-slate-700 hover:underline" data-testid="welcome-view-less">
                  Show less
                </button>
              </li>
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default WelcomeSection;
